import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootStateOrAny } from 'react-redux';
import { apiClient } from '../api.client';
import { Dispatch } from 'redux';
import { EmployeeType, LINKS } from '@trip-ux/constants';
import dayjs from 'dayjs';
import { advisorApi } from './advisorApi';

const CA = 'CA';
const CREATE_ADVISOR_STATUS = 'NEW';

// Advisor API response message constants
export const ADVISOR_RESULT_MESSAGES = {
  FOUND_IN_TRIPX: 'FOUND_IN_TRIPX',
  NOT_FOUND: 'NOT_FOUND',
  FOUND_IN_LH: 'FOUND_IN_LH',
  FOUND_IN_DSS: 'FOUND_IN_DSS',
  FAILED_TO_RETRIEVE_DSS: 'FAILED_TO_RETRIEVE_DSS',
  LH_CODE_MISMATCH_FOR_USER: 'LH_CODE_MISMATCH_FOR_USER'
} as const;

// Error message keys
export const ADVISOR_ERROR_KEYS = {
  INVALID_PARTY_ID: 'ERROR_INVALID_PARTY_ID',
  ALREADY_LINKED_PARTY_ID: 'ERROR_ALREADY_LINKED_PARTY_ID',
  INVALID_COUNSELLOR_ID: 'ERROR_INVALID_COUNSELLOR_ID',
  ALREADY_LINKED_COUNSELLOR_ID: 'ERROR_ALREADY_LINKED_COUNSELLOR_ID',
  LH_CODE_MISMATCH_ID: 'ERROR_LH_CODE_MISMATCH_ID'
} as const;

type Advisor = {
  msg: string;
  advisor?: object;
  error?: string;
};

type Sponsor = {
  sponsorCode: string;
  counselorId: string;
  partyId: string;
};

type RawSponsor = {
  LHCode: string;
  organizationName: string;
  code: string;
  contract?: {
    sponsorCode: string;
    expiryDate?: string;
  }[];
};

export interface AdvisorState {
  sponsors: { displayName: string; value: string; LHCode: string }[];
  selectedSponsors: Sponsor[];
  loading: boolean;
  registerLoading: boolean;
  error: string | null;
  advisor: Advisor;
  isAdvisorRegisterSuccess: boolean;
  isAdvisorRegisterFailure: boolean;
  advisorRegisterError: string | object | null;
  advisorResult: Advisor | null;
  advisorError: string | null;
  fieldErrors: Record<string, string>;
  formValues: { sponsorCode: string; partyId: string; counselorId: string } | null;
}

const initialState: AdvisorState = {
  sponsors: [],
  selectedSponsors: [],
  loading: false,
  registerLoading: false,
  error: null,
  advisor: {
    msg: ''
  },
  isAdvisorRegisterSuccess: false,
  isAdvisorRegisterFailure: false,
  advisorRegisterError: null,
  advisorResult: null,
  advisorError: null,
  fieldErrors: {},
  formValues: null
};

const findOrgCode = (sponsorCode, sponsorList) => {
  const orgCode = sponsorList.find(sponsor => sponsor.displayName === sponsorCode);
  return orgCode.value;
};

const formatSponsors = (selectedBranches, sponsorList) => {
  return selectedBranches.map((branch, idx) => ({
    organizationCode: findOrgCode(branch.sponsorCode, sponsorList),
    counselorCode: branch.counselorId === '' ? undefined : branch.counselorId,
    partyId: branch.partyId === '' ? undefined : branch.partyId,
    default: idx === 0,
    salesEnabled: idx === 0
  }));
};
const prepareRegisterPayload = payload => {
  const { referenceNumber, advisorProfile, profileRef, selectedSponsors, sponsors } = payload;
  return {
    referenceNumber,
    employeeType: EmployeeType.REGULAR,
    role: advisorProfile?.userGroup,
    status: CREATE_ADVISOR_STATUS,
    organizations: formatSponsors(selectedSponsors, sponsors),
    firstName: advisorProfile?.firstName,
    lastName: advisorProfile?.lastName,
    email: advisorProfile?.emailAddress,
    phone: advisorProfile?.phone?.replace(/\D/g, ''),
    addressLine1: advisorProfile?.address,
    addressLine2: advisorProfile?.address2,
    language: advisorProfile?.language,
    city: advisorProfile?.city,
    province: advisorProfile?.provinceOfResidence,
    postalCode: advisorProfile?.postalCode?.replace(/\s/g, ''),
    country: CA,
    createdBy: profileRef,
    additionalEmailRecipients: advisorProfile?.additionalEmailRecipients || []
  };
};
export const getBrokerageDisplayNames = (
  rawSponsors?: RawSponsor[]
): { displayName: string; value: string; LHCode: string }[] => {
  if (!rawSponsors) {
    return [];
  }

  const MANULIFE_DSS_ADVISORS = 'Manulife DSS Advisors';

  const advisorDisplayNames: { displayName: string; value: string; LHCode: string }[] = [];
  const sponsors = Object.values(rawSponsors);

  const dssAdvisor = sponsors.find(sponsor => sponsor.organizationName.includes(MANULIFE_DSS_ADVISORS));
  const others = sponsors.filter(sponsor => !sponsor.organizationName.includes(MANULIFE_DSS_ADVISORS));

  for (const sponsor of [dssAdvisor, ...others].filter(Boolean)) {
    const validContract = sponsor.contract?.find(
      contract => !contract.expiryDate || dayjs().isBefore(dayjs(contract.expiryDate))
    );

    const sponsorCode = validContract?.sponsorCode;
    if (sponsorCode) {
      advisorDisplayNames.push({
        displayName: `${sponsor.organizationName} - ${sponsorCode}`,
        value: sponsor.code,
        LHCode: sponsor.LHCode
      });
    }
  }
  return advisorDisplayNames;
};

/**
 * Finds an advisor by their Party ID in the DSS (Distributor Support System).
 * Used for Manulife DSS Advisors verification during the registration process.
 *
 * @param params - Object containing partyId and orgCode
 * @param params.partyId - The Party ID to search for
 * @param params.orgCode - The organization code to search within
 * @returns Advisor object if found
 * @throws Rejects with error message if advisor not found or API request fails
 */
export const findByPartyId = createAsyncThunk<
  Advisor,
  { partyId: string; orgCode: string },
  {
    state: RootStateOrAny;
    dispatch: Dispatch;
    rejectValue: string;
  }
>('/advisor/dss/find', async (params, { getState, dispatch, rejectWithValue }) => {
  try {
    const response: Advisor = await apiClient(dispatch, getState).get(LINKS.GET_ADVISOR_BY_PARTY_ID, {
      params: { partyId: params.partyId, orgCode: params.orgCode }
    });
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error && error.response?.data) {
      return rejectWithValue(error.response.data as string);
    }
    return rejectWithValue('Unknown error');
  }
});

/**
 * Finds an advisor by their Counselor ID in the LH (Liberty Health) system.
 * Used for non-DSS advisors verification during the registration process.
 *
 * @param params - Object containing counselorId and orgCode
 * @param params.counselorId - The Counselor ID to search for
 * @param params.orgCode - The organization code to search within
 * @returns Advisor object if found
 * @throws Rejects with error message if advisor not found or API request fails
 */
export const findByCounselorId = createAsyncThunk<
  Advisor,
  { counselorId: string; orgCode: string; LHCode: string },
  {
    state: RootStateOrAny;
    dispatch: Dispatch;
    rejectValue: string;
  }
>('/advisor/lh/find', async (params, { getState, dispatch, rejectWithValue }) => {
  try {
    const response: Advisor = await apiClient(dispatch, getState).get(LINKS.GET_ADVISOR_BY_COUNSELOR_ID, {
      params: { counselorId: params.counselorId, orgCode: params.orgCode, LHCode: params.LHCode }
    });
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error && error.response?.data) {
      return rejectWithValue(error.response.data as string);
    }
    return rejectWithValue('Unknown error');
  }
});

/**
 * Registers a new advisor in the system.
 * Creates a new advisor profile with their personal information, selected sponsor organizations,
 * and contact details. Formats the payload according to API requirements.
 *
 * @param payload - Object containing advisor registration data
 * @param payload.advisorProfile - Advisor's personal and contact information
 * @param payload.referenceNumber - Unique reference number for the registration
 * @param payload.selectedSponsors - Array of selected sponsor organizations
 * @returns Created advisor record from the API
 * @throws Rejects with error response data if registration fails
 */
export const registerAdvisor = createAsyncThunk(
  '/advisor',
  async (
    payload: object,
    {
      getState,
      dispatch,
      rejectWithValue
    }: { getState: () => RootStateOrAny; dispatch: Dispatch; rejectWithValue: (value: unknown) => unknown }
  ) => {
    try {
      const profileRef = getState().userProfile.userRef;
      const sponsors = getState().advisor.sponsors;
      const profilePayload = prepareRegisterPayload({ ...payload, profileRef, sponsors });
      return await apiClient(dispatch, getState).post(LINKS.CREATE_ADVISOR, { ...profilePayload });
    } catch (error) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);
const advisorSlice = createSlice({
  name: 'advisor',
  initialState,
  reducers: {
    addSelectedSponsor(state, action: { payload: Sponsor }) {
      if (!action.payload?.sponsorCode) {
        return;
      }

      const exists = state.selectedSponsors.some(s => s.sponsorCode === action.payload.sponsorCode);
      if (!exists) {
        state.selectedSponsors.unshift({
          sponsorCode: action.payload.sponsorCode,
          counselorId: action.payload.counselorId,
          partyId: action.payload.partyId
        });
      }
    },
    removeSelectedSponsor(state, action: { payload: string }) {
      state.selectedSponsors = state.selectedSponsors.filter(sponsor => sponsor.sponsorCode !== action.payload);
    },
    resetAdvisorState(state) {
      return {
        ...initialState,
        sponsors: state.sponsors
      };
    },
    clearAdvisorResult(state) {
      state.advisorResult = null;
      state.advisorError = null;
    },
    setFieldErrors(state, action: { payload: Record<string, string> }) {
      state.fieldErrors = action.payload;
    },
    clearFieldErrors(state) {
      state.fieldErrors = {};
    },
    setFormValues(state, action: { payload: { sponsorCode: string; partyId: string; counselorId: string } | null }) {
      state.formValues = action.payload;
    },
    clearFormValues(state) {
      state.formValues = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(findByPartyId.pending, state => {
        state.loading = true;
        state.error = null;
        state.advisorResult = null;
        state.advisorError = null;
      })
      .addCase(findByPartyId.fulfilled, (state, action) => {
        state.loading = false;
        state.advisor = action.payload;
        state.advisorResult = action.payload;
        state.advisorError = null;

        if (
          action.payload.error === ADVISOR_RESULT_MESSAGES.FAILED_TO_RETRIEVE_DSS ||
          action.payload.msg === ADVISOR_RESULT_MESSAGES.NOT_FOUND
        ) {
          state.fieldErrors = { partyId: ADVISOR_ERROR_KEYS.INVALID_PARTY_ID };
        } else if (action.payload.msg === ADVISOR_RESULT_MESSAGES.FOUND_IN_TRIPX) {
          state.fieldErrors = { partyId: ADVISOR_ERROR_KEYS.ALREADY_LINKED_PARTY_ID };
        } else {
          state.fieldErrors = {};
        }
      })
      .addCase(findByPartyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.advisorResult = null;
        state.advisorError = action.payload || 'Failed to find advisor by party ID';
      })
      .addCase(findByCounselorId.pending, state => {
        state.loading = true;
        state.error = null;
        state.advisorResult = null;
        state.advisorError = null;
      })
      .addCase(findByCounselorId.fulfilled, (state, action) => {
        state.loading = false;
        state.advisor = action.payload;
        state.advisorResult = action.payload;
        state.advisorError = null;

        if (
          action.payload.error === ADVISOR_RESULT_MESSAGES.FAILED_TO_RETRIEVE_DSS ||
          action.payload.msg === ADVISOR_RESULT_MESSAGES.NOT_FOUND
        ) {
          state.fieldErrors = { counselorId: ADVISOR_ERROR_KEYS.INVALID_COUNSELLOR_ID };
        } else if (action.payload.msg === ADVISOR_RESULT_MESSAGES.FOUND_IN_TRIPX) {
          state.fieldErrors = { counselorId: ADVISOR_ERROR_KEYS.ALREADY_LINKED_COUNSELLOR_ID };
        } else if (action.payload.msg === ADVISOR_RESULT_MESSAGES.LH_CODE_MISMATCH_FOR_USER) {
          state.fieldErrors = { counselorId: ADVISOR_ERROR_KEYS.LH_CODE_MISMATCH_ID };
        } else {
          state.fieldErrors = {};
        }
      })
      .addCase(findByCounselorId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.advisorResult = null;
        state.advisorError = action.payload || 'Failed to find advisor by counselor ID';
      })
      .addCase(registerAdvisor.pending, state => {
        state.registerLoading = true;
        state.isAdvisorRegisterSuccess = null;
        state.isAdvisorRegisterFailure = null;
        state.advisorRegisterError = null;
      })
      .addCase(registerAdvisor.fulfilled, state => {
        state.registerLoading = false;
        state.isAdvisorRegisterSuccess = true;
        state.isAdvisorRegisterFailure = false;
        state.advisorRegisterError = null;
      })
      .addCase(registerAdvisor.rejected, (state, action) => {
        state.registerLoading = false;
        state.isAdvisorRegisterSuccess = false;
        state.isAdvisorRegisterFailure = true;
        state.advisorRegisterError = action.payload as string | object;
      })
      .addMatcher(advisorApi.endpoints.getSponsors.matchPending, state => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(advisorApi.endpoints.getSponsors.matchFulfilled, (state, action) => {
        state.loading = false;
        state.sponsors = action.payload;
      })
      .addMatcher(advisorApi.endpoints.getSponsors.matchRejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as { error?: string; status?: string } | undefined;
        state.error = payload?.error || 'Failed to load sponsors';
      });
  }
});
export const {
  addSelectedSponsor,
  removeSelectedSponsor,
  resetAdvisorState,
  clearAdvisorResult,
  setFieldErrors,
  clearFieldErrors,
  setFormValues,
  clearFormValues
} = advisorSlice.actions;
// Selectors
export const selectAdvisorRegisterSuccess = state => state.advisor.isAdvisorRegisterSuccess;
export const selectAdvisorRegisterFailure = state => state.advisor.isAdvisorRegisterFailure;
export const selectAdvisorRegisterError = state => state.advisor.advisorRegisterError;
export default advisorSlice.reducer;
